import React from 'react'
import { Row, Divider, Col, Form, Typography, Button, Tag, Table, Empty } from 'antd'
import { Link } from 'react-router-dom'
import moment from 'moment'
import Segment from '../../../components/Segment'
import API from '../../../services/API'

const { Paragraph } = Typography

const INIT = {
    name: '',
    description: '',
    type: '',
    date_to: null,
    date_from: null,
    active: true,
    created_at: null,
    updated_at: null,
    apply_to: null,
    items_to_apply: [],
    discount_range: null,
    item_based_condition: null,
    item_based_discount: null,
    default_discount: null,
    custom_discounts: [],
    subsidiaries: [],
    customer_types: [],
}

const APPLY_TO_LABELS = {
    all_products: 'Todos los productos',
    specific_products: 'Productos específicos',
    specific_brands: 'Marcas específicas',
    specific_categories: 'Categorías específicas',
    specific_product_types: 'Tipo de producto',
    products: 'Productos',
    brands: 'Marcas',
    categories: 'Categorías',
}

const DISCOUNT_TYPE_LABELS = {
    percentage: 'Porcentaje',
    fixed: 'Precio',
    bogo_discount: 'Paga y llévate',
}

const BOGO_OPTION_LABELS = {
    same_product: 'Mismo producto',
    other_product: 'Otro producto',
}

const formatRangeValue = (range) => {
    if (range.discount_type === 'bogo_discount') {
        const option = range.discount_bogo_option && range.discount_bogo_option.key
            ? BOGO_OPTION_LABELS[range.discount_bogo_option.key] || range.discount_bogo_option.key
            : ''
        return `${range.discount_bogo_quantity || 0} gratis (${range.discount_bogo_percent || 0}%) - ${option}`
    }
    if (range.discount_type === 'percentage') {
        return `${range.value}%`
    }
    return `$${range.value}`
}

const DiscountsShowContainer = ({ match }) => {
    const [loading, setloading] = React.useState(false)
    const [data, setData] = React.useState(INIT)

    const getData = () => {
        setloading(true)
        API.get(`/api/discounts/${match.params.id}`)
            .then(response => {
                setData({ ...INIT, ...response.data })
            })
            .catch(error => {
                console.error('Error en obtener el descuento', error)
            })
            .finally(() => {
                setloading(false)
            })
    }
    React.useEffect(() => {
        getData()
    }, [])

    const applyTo = data.type === 'item_based'
        ? (data.item_based_condition && data.item_based_condition.apply_to)
        : data.apply_to
    const showItemsToApply = applyTo && applyTo !== 'all_products' && data.items_to_apply && data.items_to_apply.length !== 0

    const ranges = data.discount_range ? Object.values(data.discount_range) : []

    const rangeColumns = [
        {
            title: 'Cant. mín',
            dataIndex: 'min_quantity',
            key: 'min_quantity',
        },
        {
            title: 'Cant. máx',
            dataIndex: 'max_quantity',
            key: 'max_quantity',
        },
        {
            title: 'Tipo de descuento',
            dataIndex: 'discount_type',
            key: 'discount_type',
            render: (value) => DISCOUNT_TYPE_LABELS[value] || value,
        },
        {
            title: 'Valor',
            key: 'value',
            render: (_, range) => formatRangeValue(range),
        },
    ]

    return (
        <Segment loading={loading}>
            <Divider>Detalle del descuento</Divider>
            <Form style={{ maxWidth: '500px', margin: 'auto' }} className="form-border">
                <Row gutter={24}>
                    <Col lg={12} md={12} sm={24}>
                        <Paragraph strong>Nombre:</Paragraph>
                    </Col>
                    <Col lg={12} md={12} sm={24}>
                        <Paragraph>{data.name}</Paragraph>
                    </Col>
                </Row>

                <Row gutter={24}>
                    <Col lg={12} md={12} sm={24}>
                        <Paragraph strong>Descripción:</Paragraph>
                    </Col>
                    <Col lg={12} md={12} sm={24}>
                        <Paragraph>{data.description}</Paragraph>
                    </Col>
                </Row>

                <Row gutter={24}>
                    <Col lg={12} md={12} sm={24}>
                        <Paragraph strong>Tipo:</Paragraph>
                    </Col>
                    <Col lg={12} md={12} sm={24}>
                        <Paragraph>
                            {data.type == 'quantity_based' ? 'Basado en cantidad' : ''}
                            {data.type == 'item_based' ? 'Basado en articulos' : ''}
                            {data.type == 'mixed' ? 'Descuento mixto' : ''}
                        </Paragraph>
                    </Col>
                </Row>

                <Row gutter={24}>
                    <Col lg={12} md={12} sm={24}>
                        <Paragraph strong>Fecha inicio:</Paragraph>
                    </Col>
                    <Col lg={12} md={12} sm={24}>
                        {data.date_from ? moment(data.date_from).format('DD/MM/YYYY') : ''}
                    </Col>
                </Row>

                <Row gutter={24}>
                    <Col lg={12} md={12} sm={24}>
                        <Paragraph strong>Fecha fin:</Paragraph>
                    </Col>
                    <Col lg={12} md={12} sm={24}>
                        {data.date_to ? moment(data.date_to).format('DD/MM/YYYY') : ''}
                    </Col>
                </Row>

                <Row gutter={24}>
                    <Col lg={12} md={12} sm={24}>
                        <Paragraph strong>Activo:</Paragraph>
                    </Col>
                    <Col lg={12} md={12} sm={24}>
                        <Paragraph>{data.active ? 'Si' : 'No'}</Paragraph>
                    </Col>
                </Row>

                <Row gutter={24}>
                    <Col lg={12} md={12} sm={24}>
                        <Paragraph strong>Aplica a:</Paragraph>
                    </Col>
                    <Col lg={12} md={12} sm={24}>
                        <Paragraph>{applyTo ? (APPLY_TO_LABELS[applyTo] || applyTo) : ''}</Paragraph>
                    </Col>
                </Row>

                {showItemsToApply && (
                    <Row gutter={24}>
                        <Col lg={12} md={12} sm={24}>
                            <Paragraph strong>Productos que aplican:</Paragraph>
                        </Col>
                        <Col lg={12} md={12} sm={24}>
                            {data.items_to_apply.map((item) => (
                                <Tag key={item.key || item.value || item.id}>{item.label || item.title || item.name}</Tag>
                            ))}
                        </Col>
                    </Row>
                )}

                <Row gutter={24}>
                    <Col lg={12} md={12} sm={24}>
                        <Paragraph strong>Sucursales:</Paragraph>
                    </Col>
                    <Col lg={12} md={12} sm={24}>
                        {data.subsidiaries && data.subsidiaries.length !== 0 ? (
                            data.subsidiaries.map((subsidiary) => (
                                <Tag key={subsidiary.id}>{subsidiary.name}</Tag>
                            ))
                        ) : (
                            <Paragraph>Todas</Paragraph>
                        )}
                    </Col>
                </Row>

                <Row gutter={24}>
                    <Col lg={12} md={12} sm={24}>
                        <Paragraph strong>Tipos de cliente:</Paragraph>
                    </Col>
                    <Col lg={12} md={12} sm={24}>
                        {data.customer_types && data.customer_types.length !== 0 ? (
                            data.customer_types.map((customerType) => (
                                <Tag key={customerType.id}>{customerType.name}</Tag>
                            ))
                        ) : (
                            <Paragraph>Todos</Paragraph>
                        )}
                    </Col>
                </Row>

                <Row gutter={24}>
                    <Col lg={12} md={12} sm={24}>
                        <Paragraph strong>Creación:</Paragraph>
                    </Col>
                    <Col lg={12} md={12} sm={24}>
                        {data.created_at ? moment(data.created_at).format('DD/MM/YYYY hh:mm A') : ''}
                    </Col>
                </Row>

                <Row gutter={24}>
                    <Col lg={12} md={12} sm={24}>
                        <Paragraph strong>Actualización:</Paragraph>
                    </Col>
                    <Col lg={12} md={12} sm={24}>
                        {data.updated_at ? moment(data.updated_at).format('DD/MM/YYYY hh:mm A') : ''}
                    </Col>
                </Row>
            </Form>

            {data.type === 'quantity_based' && (
                <>
                    <Divider>Rangos de descuento</Divider>
                    {ranges.length !== 0 ? (
                        <Table
                            style={{ maxWidth: '900px', margin: 'auto' }}
                            rowKey={(range, index) => index}
                            columns={rangeColumns}
                            dataSource={ranges}
                            pagination={false}
                        />
                    ) : (
                        <Empty description="Sin rangos configurados" />
                    )}
                </>
            )}

            {data.type === 'mixed' && (
                <>
                    <Divider>Descuento general</Divider>
                    <Form style={{ maxWidth: '500px', margin: 'auto' }} className="form-border">
                        <Row gutter={24}>
                            <Col lg={12} md={12} sm={24}>
                                <Paragraph strong>Porcentaje:</Paragraph>
                            </Col>
                            <Col lg={12} md={12} sm={24}>
                                <Paragraph>
                                    {data.default_discount && data.default_discount.value !== undefined
                                        ? `${data.default_discount.value}%`
                                        : 'Sin definir'}
                                </Paragraph>
                            </Col>
                        </Row>
                    </Form>

                    <Divider>Descuentos personalizados</Divider>
                    {data.custom_discounts && data.custom_discounts.length !== 0 ? (
                        data.custom_discounts.map((custom, index) => (
                            <div
                                // eslint-disable-next-line react/no-array-index-key
                                key={index}
                                style={{
                                    maxWidth: '700px',
                                    margin: '0 auto 16px',
                                    border: '1px solid #e8e8e8',
                                    borderRadius: '6px',
                                    padding: '16px',
                                    textAlign: 'left',
                                }}
                            >
                                <Paragraph strong>Productos:</Paragraph>
                                {(custom.selected_products || []).map((product) => (
                                    <Tag key={product.id}>{product.name}</Tag>
                                ))}
                                {custom.discount_kind === 'percentage' ? (
                                    <Paragraph style={{ marginTop: 8, marginBottom: 0 }}>
                                        {`Descuento: ${custom.percentage_value}%`}
                                    </Paragraph>
                                ) : (
                                    <Paragraph style={{ marginTop: 8, marginBottom: 0 }}>
                                        {`Cantidad ${custom.range_min} - ${custom.range_max}: ${
                                            custom.range_discount_type === 'percentage'
                                                ? `${custom.range_value}%`
                                                : `$${custom.range_value}`
                                        }`}
                                    </Paragraph>
                                )}
                            </div>
                        ))
                    ) : (
                        <Empty description="Sin descuentos personalizados configurados" />
                    )}
                </>
            )}

            {data.type === 'item_based' && data.item_based_condition && (
                <>
                    <Divider>Condición</Divider>
                    <Form style={{ maxWidth: '500px', margin: 'auto' }} className="form-border">
                        <Row gutter={24}>
                            <Col lg={12} md={12} sm={24}>
                                <Paragraph strong>En la compra de:</Paragraph>
                            </Col>
                            <Col lg={12} md={12} sm={24}>
                                <Paragraph>{APPLY_TO_LABELS[data.item_based_condition.apply_to] || data.item_based_condition.apply_to}</Paragraph>
                            </Col>
                        </Row>
                        {data.item_based_condition.items_to_buy && data.item_based_condition.items_to_buy.length !== 0 && (
                            <Row gutter={24}>
                                <Col lg={12} md={12} sm={24}>
                                    <Paragraph strong>Productos a comprar:</Paragraph>
                                </Col>
                                <Col lg={12} md={12} sm={24}>
                                    {data.item_based_condition.items_to_buy.map((item) => (
                                        <Tag key={item.id}>{item.name}</Tag>
                                    ))}
                                </Col>
                            </Row>
                        )}
                        {data.item_based_discount && (
                            <Row gutter={24}>
                                <Col lg={12} md={12} sm={24}>
                                    <Paragraph strong>Descuento:</Paragraph>
                                </Col>
                                <Col lg={12} md={12} sm={24}>
                                    <Paragraph>
                                        {DISCOUNT_TYPE_LABELS[data.item_based_discount.discount_type] || data.item_based_discount.discount_type}
                                        {' - '}
                                        {data.item_based_discount.discount_type === 'percentage' ? `${data.item_based_discount.discount_value}%` : `$${data.item_based_discount.discount_value}`}
                                    </Paragraph>
                                </Col>
                            </Row>
                        )}
                    </Form>
                </>
            )}

            <Row type="flex" justify="center" style={{ marginTop: '50px' }}>
                <Form.Item>
                    <Link to="/discounts" style={{ marginLeft: '5px' }}>
                        <Button icon="arrow-left">
                            Regresar
                        </Button>
                    </Link>
                </Form.Item>
            </Row>
        </Segment>
    )
}

export default DiscountsShowContainer
